import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { Theme } from '@theme'
import { getFormatedList, localizedReferenceName } from '@utils'
import dayjs from 'dayjs'
import { DataItem, useScreen } from '@hooks'
import { useLanguage } from '@context'

interface GhostPresentationSectionProps {
  birthday?: string
  languages?: DataItem[]
  nationalities?: DataItem[]
  origins?: DataItem[]
}

export const GhostPresentationSection: React.FC<GhostPresentationSectionProps> = ({
  birthday,
  languages,
  nationalities,
  origins,
}) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyle(theme)
  const { isWeb } = useScreen()

  return (
    <View style={styles.container}>
      <View style={styles.topContainer}>
        <Text type="bodyBold" color={theme.colors.primary} style={styles.title}>
          {isWeb ? t('ghostProfile.about') : t('ghostProfile.presentation')}
        </Text>
      </View>

      <View style={styles.sectionContainer}>
        {/* Anniversaire */}
        <View style={styles.sectionRow}>
          <View style={styles.leftContent}>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
              {t('ghostProfile.birthday')}
            </Text>
            <Text type="bodyBold" color={theme.colors.secondary}>
              {!birthday? '' : dayjs(birthday, 'YYYY-MM-DD').format('DD MMMM YYYY')}
            </Text>
          </View>
          <View style={styles.iconContainer}>
            <Icon 
              name="calendarStar" 
              color={theme.colors.primary} 
              size={theme.iconSize.large}
            />
          </View>
        </View>

        {/* Langues */}
        <View style={styles.sectionRow}>
          <View style={styles.leftContent}>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
              {t('ghostProfile.languages')}
            </Text>
            <Text type="bodyBold" color={theme.colors.secondary}>
              {getFormatedList(
                languages?.map((language) => language.name ?? localizedReferenceName(currentLanguage, language.nameFr, language.nameEn, language.nameEs)) ||
                  [],
              )}
            </Text>
          </View>
          <View style={styles.iconContainer}>
            <Icon 
              name="translate" 
              color={theme.colors.primary} 
              size={theme.iconSize.large}
            />
          </View>
        </View>

        {/* Nationalité */}
        <View style={styles.sectionRow}>
          <View style={styles.leftContent}>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
              {t('ghostProfile.nationalities')}
            </Text>
            <Text type="bodyBold" color={theme.colors.secondary}>
              {getFormatedList(
                nationalities?.map(
                  (nationality) => nationality.name ?? localizedReferenceName(currentLanguage, nationality.nameFr, nationality.nameEn, nationality.nameEs),
                ) || [],
              )}
            </Text>
          </View>
          <View style={styles.iconContainer}>
            <Icon 
              name="user" 
              color={theme.colors.secondary} 
              size={theme.iconSize.large}
            />
          </View>
        </View>

        {/* Origines */}
        <View style={[styles.sectionRow, styles.lastRow]}>
          <View style={styles.leftContent}>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.sectionTitle}>
              {t('ghostProfile.origins')}
            </Text>
            <Text type="bodyBold" color={theme.colors.secondary}>
              {getFormatedList(
                origins?.map((origin) => origin.name ?? localizedReferenceName(currentLanguage, origin.nameFr, origin.nameEn, origin.nameEs)) || [],
              )}
            </Text>
          </View>
          <View style={styles.iconContainer}>
            <Icon 
              name="globe" 
              color={theme.colors.primary} 
              size={theme.iconSize.large}
            />
          </View>
        </View>
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: theme.spacing.xl,
    },
    topContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.sm,
    },
    title: {
      margin: 0,
    },
    sectionContainer: {
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.border.large,
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    sectionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.lg,
    },
    lastRow: {
      marginBottom: 0,
    },
    leftContent: {
      flex: 1,
    },
    sectionTitle: {
      marginBottom: theme.spacing.xs,
    },
    iconContainer: {
      marginLeft: theme.spacing.md,
    },
  }) 